// NATIONAL INDICATORS
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';

import SectionWithArrows from 'components/organisms/SectionWithArrows/SectionWithArrows';
import { StatisticsService } from 'services';

import { LocalIndicatorsContext } from './contexts';
import {
    ChartEight,
    ChartFive,
    ChartNine,
    ChartOne,
    ChartSeven,
    ChartSix,
    ChartTen,
    ChartThree,
    ChartTwo,
    ChartFour,
    CustomTable, ChartEleven
} from './components';
import clsx from 'clsx';
import Loader from 'components/organisms/Loader';
import {useTranslation} from "react-i18next";

const useStyles = makeStyles((theme) => ({
    root: {},
    content: {
        minHeight: 450,
        '& .section': {
            paddingTop: theme.spacing(4),
        },
    },
    filtersContainer: {
        marginBottom: theme.spacing(1),
    },
    chartContainer: {
        boxShadow: '0 0 4px #00000029',
        borderRadius: '10px',
        marginBottom: '20px',
        marginTop: '20px',
    },
    container: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            flexWrap: 'wrap',
        },
    },
    rightContainer: {
        width: '250px',
    },
    leftContainer: {
        width: '75%',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    divider: {
        height: 5,
        marginTop: 50,
        marginBottom: 30,
    },
    dictionary: {
        boxShadow: '0px 3px 6px #00000029',
        borderRadius: '10px',
        backgroundColor: '#f6f6f6',
        padding: theme.spacing(4),
        marginTop: theme.spacing(6),
        width: '280px',
    },
    dictionaryTitle: {
        marginBottom: 0,
        alignSelf: 'flex-end',
    },
    dictionaryHeader: {
        display: ' flex',
        flexDirection: 'row',
        marginBottom: 20,
    },
    dictionaryTerm: {
        fontStyle: 'italic',
        fontSize: 12,
    },
    dictionaryParagraph: {
        display: 'inline',
        marginBottom: 10,
        fontSize: 12,
    },
    filterText: {
        fontSize: '18px',
        marginLeft: '12px',
        marginBottom: '10px',
    },
    title: {
        fontSize: '24px',
        fontStyle: 'Roboto-Regular',
        textAlign: 'center',
        color: '#981B49',
    },
    chartButton: {
        width: '282px',
        fontWeight: theme.typography.fontWeightBold,
        fontSize: 15,
        marginTop: theme.spacing(5),
        color: '#981B49',
        backgroundColor: 'white',
        boxShadow: '0px 3px 6px #00000029',
        '&:hover': {
            color: 'white',
            backgroundColor: 'black',
        },
    },
    chartMargin: {
        marginTop: '50px',
    },
    highlightedText: {
        fontSize: '24px',
        fontStyle: 'Roboto-Regular',
        fontWeight: 'bold',
        color: '#2d3748',
        backgroundImage: 'linear-gradient(90deg, transparent 60%, #981B49 0)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 0.2em', // Thickness of the underline
        backgroundPosition: '0 100% ',  // Position of the underline
    },
    parentContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    }
}));

const LocalIndicators = (props) => {
    const { t } = useTranslation();
    const { className } = props;
    const classes = useStyles();
    const statisticsService = new StatisticsService();

    const [chartData, setChartData] = useState([]);
    const [spinner, setSpinner] = useState(true);

    // One time only
    useEffect(() => {
        setSpinner(true);
        (async () => {
            const newData = await statisticsService.getLocalIndicatorChartData();
            setChartData(newData['data']);
            setSpinner(false);
        })();
    }, []);

    return (
        <LocalIndicatorsContext.Provider value={{}} >
            {spinner ? (
                <Loader />
            ) : (
                <div className={clsx(classes.root, className)}>
                    <SectionWithArrows className={clsx(classes.content)} statisticsWidth={true}>
                        <div>
                            <CustomTable legendData={chartData['main_legend']} headerOne={t('LOCAL_INDICATORS.POPULATION')}/>
                        </div>
                        <div>
                            <div className={classes.chartMargin}>
                                <div className={clsx(classes.title)}>
                                    {chartData['chart_eleven']['title']}
                                </div>
                                <ChartEleven data={chartData['chart_eleven']} name={'chartEleven'}
                                          className={classes.chartContainer}/>
                            </div>
                            <div className={classes.chartMargin}>
                                <div className={clsx(classes.title)}>
                                    {chartData['chart_two']['title']}
                                </div>
                                <ChartTwo data={chartData['chart_two']} name={'chartTwo'}
                                          className={classes.chartContainer}/>
                            </div>
                            <div className={classes.chartMargin}>
                                <div className={clsx(classes.title)}>
                                    {chartData['chart_four']['title']}
                                </div>
                                <ChartFour data={chartData['chart_four']} name={'chartFour'}
                                           className={classes.chartContainer}/>
                            </div>
                            <div className={classes.chartMargin}>
                                <div className={clsx(classes.title)}>
                                    {chartData['chart_eight']['title']}
                                </div>
                                <ChartEight data={chartData['chart_eight']} name={'chartEight'}
                                            className={classes.chartContainer}/>
                            </div>
                            <div className={classes.chartMargin}>
                                <div className={clsx(classes.title)}>
                                    {chartData['chart_five']['title']}
                                </div>
                                <ChartFive data={chartData['chart_five']} name={'chartFive'}
                                           className={classes.chartContainer}/>
                            </div>
                            <div>
                                <CustomTable legendData={chartData['arrears_legend']} headerOne={t('LOCAL_INDICATORS.STATUS')}/>
                            </div>
                            <div className={classes.chartMargin}>
                                <div className={clsx(classes.title)}>
                                    {chartData['chart_nine']['title']}
                                </div>
                                <ChartNine data={chartData['chart_nine']} name={'chartNine'}
                                           className={classes.chartContainer}/>
                            </div>
                            <div className={classes.chartMargin}>
                                <div className={clsx(classes.title)}>
                                    {chartData['chart_ten']['title']}
                                </div>
                                <ChartTen data={chartData['chart_ten']} name={'chartTen'}
                                          className={classes.chartContainer}/>
                            </div>
                            <div className={classes.chartMargin}>
                                <div className={clsx(classes.title)}>
                                    {chartData['chart_one']['title']}
                                </div>
                                <ChartOne data={chartData['chart_one']} name={'chartOne'}
                                          className={classes.chartContainer}/>
                            </div>
                            <div className={classes.chartMargin}>
                                <div className={clsx(classes.title)}>
                                    {chartData['chart_three']['title']}
                                </div>
                                <ChartThree data={chartData['chart_three']} name={'chartThree'}
                                            className={classes.chartContainer}/>
                            </div>
                            <div className={classes.chartMargin}>
                                <div className={clsx(classes.title)}>
                                    {chartData['chart_six']['title']}
                                </div>
                                <ChartSix data={chartData['chart_six']} name={'chartSix'}
                                          className={classes.chartContainer}/>
                            </div>
                            <div className={classes.chartMargin}>
                                <div className={clsx(classes.title)}>
                                    {chartData['chart_seven']['title']}
                                </div>
                                <ChartSeven data={chartData['chart_seven']} name={'chartSeven'}
                                            className={classes.chartContainer}/>
                            </div>
                        </div>
                    </SectionWithArrows>
                </div>
            )}
        </LocalIndicatorsContext.Provider>
    );
};

LocalIndicators.propTypes = {
    /**
     * External classes
     */
    className: PropTypes.string,
};

export default LocalIndicators;
